import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FormContainer from "../../../components/FormContainer";
import Karyawan from '../../../models/Karyawan';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import Divisi from '../../../models/Divisi';
import Posisi from '../../../models/Posisi';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import { validate as uuidValidate } from "uuid";

const groupName: MenuGroupName = "Umum";
const subgroupName: string = "Master";
const code: MenuCode = "GLOBAL-KARYAWAN";
const title: MenuTitle = "Karyawan";
const endpoint = "karyawan";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Karyawan>({});
    const divisiRef = useRef();
    const posisiRef = useRef();
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
                const data: Karyawan | null = response.data?.data ?
                {
                    ...response.data?.data,
                    divisi: response.data.data.ucode_divisi ?
                        {
                            ucode: response.data.data.ucode_divisi,
                            kode: response.data.data.kode_divisi,
                            nama: response.data.data.nama_divisi,
                            aktif: response.data.data.aktif,
                            keterangan: response.data.data.keterangan,
                        }
                        :
                        undefined,
                    posisi: response.data.data.ucode_posisi ?
                        {
                            ucode: response.data.data.ucode_posisi,
                            kode: response.data.data.kode_posisi,
                            nama: response.data.data.nama_posisi,
                            aktif: response.data.data.aktif,
                            keterangan: response.data.data.keterangan,
                        }
                        :
                        undefined,
                }
                :
                null;

                setFormData(data? {...data} : {});
            })
            .catch((error) => console.error({error}))
            .finally(() => {
                setLoading(false);
            })
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Karyawan | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data?.kode)
            return false;

        if (!data?.ucode_divisi)
            return false;

        if (!data?.ucode_posisi)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Karyawan> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);
        console.log({param});

        const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
            ucode_divisi: param.data.ucode_divisi,
            ucode_posisi: param.data.ucode_posisi,
            email: param.data.email,
            telp: param.data.telp,
            alamat: param.data.alamat,
            keterangan: param.data.keterangan,
        };

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<Karyawan>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    isViewMode={isViewMode}
                    fields={[
                        {
                            name: "kode",
                            title: "Kode Karyawan",
                            inputType: "text",
                            dataType: (typeof formData.kode),
                            isReadonly: false,
                            isRequired: formData?.nama ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                        },
                        {
                            name: "nama",
                            title: "Nama Karyawan",
                            inputType: "text",
                            dataType: (typeof formData.nama),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                        },
                        {
                            name: "divisi",
                            keyField: "ucode",
                            labelField: "nama",
                            editKeyField: "ucode_divisi",
                            title: "Divisi",
                            inputType: "combobox",
                            dataType: (typeof Divisi),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                            searchEndpoint: "divisi/nama",
                            searchEndpointFilterParamName: "nama",
                            ref: divisiRef,
                        },
                        {
                            name: "posisi",
                            keyField: "ucode",
                            labelField: "nama",
                            editKeyField: "ucode_posisi",
                            title: "Posisi",
                            inputType: "combobox",
                            dataType: (typeof Posisi),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                            searchEndpoint: "posisi/nama",
                            searchEndpointFilterParamName: "nama",
                            ref: posisiRef,
                        },
                        {
                            name: "email",
                            title: "Email",
                            inputType: "text",
                            dataType: (typeof formData.email),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                        },
                        {
                            name: "telp",
                            title: "Telepon/HP",
                            inputType: "text",
                            dataType: (typeof formData.telp),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                        },
                        {
                            name: "alamat",
                            title: "Alamat",
                            inputType: "textarea",
                            dataType: (typeof formData.alamat),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                            rows: 3,
                        },
                        {
                            name: "keterangan",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan),
                            isReadonly: false,
                            isRequired: false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Karyawan>) => onChanged(valueObj),
                            rows: 3,
                        },
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
            </FormContainer>
        </>
    );
};

export default Form;
